import { Controller } from "stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"

export default class extends Controller {
  static targets = ["calendar", "instructorFilter", "modal", "modalTitle", "modalBody", "form"]
  static values = {
    calendarDataUrl: String,
    createUrl: String,
    updateUrl: String,
    deleteUrl: String,
    editable: { type: Boolean, default: true },
    timeZone: String,
    initialView: { type: String, default: "timeGridWeek" },
    initialDate: String
  }

  connect() {
    this.initializeCalendar()
    this.setupEventListeners()
    this.setupCustomNavigation()
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy()
    }
  }

  initializeCalendar() {
    const calendarEl = this.calendarTarget

    // Check sessionStorage for saved state, otherwise use data attributes
    const savedDate = sessionStorage.getItem('availability_calendar_start_date')
    const savedView = sessionStorage.getItem('availability_calendar_view')

    const initialDate = savedDate ? new Date(savedDate) : (this.hasInitialDateValue ? new Date(this.initialDateValue) : undefined)
    const initialView = savedView ? this.paramToViewType(savedView) : this.initialViewValue

    this.calendar = new Calendar(calendarEl, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: initialView,
      initialDate: initialDate,
      timeZone: this.timeZoneValue || "local",
      headerToolbar: false, // Disable built-in header, use custom navigation
      editable: this.editableValue,
      selectable: this.editableValue,
      selectMirror: true,
      dayMaxEvents: true,
      height: 800,
      slotMinTime: "00:00:00",
      slotMaxTime: "24:00:00",
      slotDuration: "00:15:00",
      snapDuration: "00:15:00",
      scrollTime: "06:00:00",
      nowIndicator: true,
      allDaySlot: false,

      // Event sources
      events: this.calendarDataUrlValue,

      // Interaction handlers
      select: this.handleDateSelect.bind(this),
      eventClick: this.handleEventClick.bind(this),
      eventDrop: this.handleEventDrop.bind(this),
      eventResize: this.handleEventResize.bind(this),

      // Loading indicator
      loading: this.handleLoading.bind(this),

      // Update custom header when view changes
      datesSet: this.updateCustomHeader.bind(this)
    })

    this.calendar.render()
    this.addEventStyles()
  }

  addEventStyles() {
    // Create a style element for event text wrapping
    const style = document.createElement('style')
    style.textContent = `
      .fc-event-title {
        white-space: pre-wrap !important;
        word-wrap: break-word !important;
        line-height: 1.2 !important;
        padding: 2px 4px !important;
      }
      .fc-daygrid-event-frame {
        min-height: auto !important;
      }
      .fc-daygrid-event {
        min-height: auto !important;
      }
    `
    document.head.appendChild(style)
  }

  setupEventListeners() {
    // Instructor filter
    if (this.hasInstructorFilterTarget) {
      this.instructorFilterTarget.addEventListener("change", this.filterByInstructor.bind(this))
    }
  }

  handleDateSelect(selectInfo) {
    if (!this.editableValue) return

    // Open modal or directly create
    const start = selectInfo.start
    const end = selectInfo.end

    // For now, show a simple prompt (will be replaced with a modal form)
    this.showCreateModal(start, end)

    this.calendar.unselect()
  }

  handleEventClick(clickInfo) {
    if (!this.editableValue) return

    const event = clickInfo.event

    // Show edit/delete modal
    this.showEditModal(event)
  }

  handleEventDrop(dropInfo) {
    if (!this.editableValue) return

    const event = dropInfo.event

    this.updateEvent(event)
  }

  handleEventResize(resizeInfo) {
    if (!this.editableValue) return

    const event = resizeInfo.event

    this.updateEvent(event)
  }

  handleLoading(isLoading) {
    // Show/hide loading indicator
    if (isLoading) {
      this.calendarTarget.classList.add("loading")
    } else {
      this.calendarTarget.classList.remove("loading")
    }
  }

  showCreateModal(start, end) {
    // Use FullCalendar's formatIso to format in the calendar's timezone
    const startStr = this.formatDateTimeForForm(start)
    const endStr = this.formatDateTimeForForm(end)

    // Build the form URL with pre-filled data
    const url = `${this.createUrlValue}?start_at=${encodeURIComponent(startStr)}&end_at=${encodeURIComponent(endStr)}`

    // Navigate to the form or open in modal
    window.location.href = url
  }

  showEditModal(event) {
    const blockId = event.id
    const blockType = event.extendedProps?.blockType || "availability"

    // Determine the correct edit URL based on block type
    let editUrl
    if (blockType === "unavailability") {
      // Route to unavailability block edit page
      editUrl = this.updateUrlValue.replace(":id", blockId).replace("availability_blocks", "unavailability_blocks")
    } else {
      // Route to availability block edit page
      editUrl = this.updateUrlValue.replace(":id", blockId)
    }

    // Navigate to edit form
    window.location.href = editUrl
  }

  updateEvent(event) {
    const blockId = event.id
    const blockType = event.extendedProps?.blockType || "availability"
    const updateUrl = this.updateUrlValue.replace(":id", blockId) + ".json"

    // Determine which param name to use based on block type
    const paramName = blockType === "unavailability"
      ? "instructor_unavailability_block"
      : "instructor_availability_block"

    // Format dates in local timezone (not UTC)
    // FullCalendar gives us Date objects in the calendar's timezone
    const data = {
      [paramName]: {
        start_at: this.formatDateForServer(event.start),
        end_at: this.formatDateForServer(event.end)
      }
    }

    fetch(updateUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.getCSRFToken()
      },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then(data => {
      if (data.errors) {
        alert("Error updating block: " + data.errors.join(", "))
        this.calendar.refetchEvents()
      } else {
        // Success - event already updated in UI
        console.log("Block updated successfully")
      }
    })
    .catch(error => {
      console.error("Error:", error)
      alert("Failed to update " + blockType + " block")
      this.calendar.refetchEvents()
    })
  }

  filterByInstructor(event) {
    const instructorId = event.target.value

    if (instructorId) {
      // Filter calendar events by instructor
      this.calendar.getEvents().forEach(calEvent => {
        const eventInstructorId = calEvent.extendedProps.instructorId
        if (eventInstructorId && eventInstructorId.toString() === instructorId) {
          calEvent.setProp("display", "auto")
        } else {
          calEvent.setProp("display", "none")
        }
      })
    } else {
      // Show all events
      this.calendar.getEvents().forEach(calEvent => {
        calEvent.setProp("display", "auto")
      })
    }
  }

  formatDateTimeForForm(date) {
    // Use FullCalendar's formatIso to get the time in the calendar's configured timezone
    // This returns ISO format like "2025-10-16T14:30:00", so we trim off the seconds
    const isoStr = this.calendar.formatIso(date, { omitTime: false, omitZone: true })
    // Return format expected by datetime-local input: "2025-10-16T14:30"
    return isoStr.substring(0, 16)
  }

  formatDateForServer(date) {
    // Format date in the calendar's timezone (not browser's local timezone)
    // FullCalendar provides dates in the configured timezone
    // Use the calendar API to format in that timezone
    return this.calendar.formatIso(date, { omitTime: false, omitZone: true })
  }

  getCSRFToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  refreshCalendar() {
    if (this.calendar) {
      this.calendar.refetchEvents()
    }
  }

  setupCustomNavigation() {
    // Set up click handlers for custom navigation buttons
    document.addEventListener('click', (e) => {
      // Find the button with data-calendar-action, even if clicking on child element (like icon)
      const button = e.target.closest('[data-calendar-action]')
      if (button) {
        e.preventDefault()
        this.handleNavigationClick(button.dataset.calendarAction)
      }
    })
  }

  handleNavigationClick(action) {
    if (!this.calendar) return

    switch(action) {
      case 'prev':
        this.calendar.prev()
        this.updateUrl()
        break
      case 'next':
        this.calendar.next()
        this.updateUrl()
        break
      case 'today':
        this.calendar.today()
        this.updateUrl()
        break
      case 'dayGridMonth':
      case 'timeGridWeek':
      case 'timeGridDay':
        this.calendar.changeView(action)
        this.updateUrl()
        break
    }
  }

  updateCustomHeader() {
    if (!this.calendar) return

    // Update the main title
    const titleEl = document.querySelector('[data-calendar-title]')
    if (titleEl) {
      // Format the title to make the year lighter weight (matching the classes calendar style)
      const fullTitle = this.calendar.view.title
      // Match patterns like "October 2025", "Oct 28 - Nov 3, 2025", etc. and wrap the year in fw-light span
      const formattedTitle = fullTitle.replace(/(\d{4})/, "<span class='fw-light'>$1</span>")
      titleEl.innerHTML = formattedTitle
    }

    // Update the date display in the navigation controls
    const dateDisplayEl = document.querySelector('[data-calendar-date-display]')
    if (dateDisplayEl) {
      const currentDate = this.calendar.getDate()
      // Format: "mm/dd/yyyy"
      const month = String(currentDate.getMonth() + 1).padStart(2, '0')
      const day = String(currentDate.getDate()).padStart(2, '0')
      const year = currentDate.getFullYear()
      dateDisplayEl.textContent = `${month}/${day}/${year}`
    }

    // Update active state on view buttons
    const currentView = this.calendar.view.type
    document.querySelectorAll('[data-calendar-action^="timeGrid"], [data-calendar-action^="dayGrid"]').forEach(btn => {
      if (btn.dataset.calendarAction === currentView) {
        btn.classList.add('active_calendar')
      } else {
        btn.classList.remove('active_calendar')
      }
    })
  }

  updateUrl() {
    // Update URL with current view and date parameters
    const currentDate = this.calendar.getDate()
    const currentView = this.calendar.view.type

    const url = new URL(window.location)
    url.searchParams.set('view', this.viewTypeToParam(currentView))
    url.searchParams.set('start_date', currentDate.toISOString().split('T')[0])

    window.history.replaceState({}, '', url)

    // Also save to sessionStorage so it persists when navigating away and back
    sessionStorage.setItem('availability_calendar_view', this.viewTypeToParam(currentView))
    sessionStorage.setItem('availability_calendar_start_date', currentDate.toISOString().split('T')[0])
  }

  viewTypeToParam(viewType) {
    const mapping = {
      'timeGridDay': 'day',
      'timeGridWeek': 'week',
      'dayGridMonth': 'month'
    }
    return mapping[viewType] || 'week'
  }

  paramToViewType(param) {
    const mapping = {
      'day': 'timeGridDay',
      'week': 'timeGridWeek',
      'month': 'dayGridMonth'
    }
    return mapping[param] || 'timeGridWeek'
  }
}
